import {
  Breadcrumbs,
  CardProps,
  Chip,
  Divider,
  IconButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/joy';
import {
  Dot,
  DotsThree,
  PencilSimpleLine,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWRInfinite from 'swr/infinite';
import { z } from 'zod';

import { useProcessForm } from './ProcessesTab';
import {
  SelectAssignee,
  SelectDueDate,
  SelectPriority,
  SelectStatus,
} from './ProcessManagement';
import {
  AttachFilebutton,
  ReportAttachmentItem,
} from './TaskDialog/ReportAttachment';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FieldGroup, Label } from '~/components/ui/fieldset';
import { Field, useFormController } from '~/components/ui/form';
import { InputField, inputFieldClasses } from '~/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import {
  EditorControls,
  FontSizeMenu,
  HeadingMenu,
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { LoadingWrapper } from '~/features/process-management/Common';
import { formatDateString } from '~/features/process-management/configs';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ProcessService } from '~/services/api/processes/endpoints';
import { TaskComment } from '~/services/api/processes/types';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import {
  getErrorMessage,
  isFetchBaseQueryError,
} from '~/services/api/rest-api-error';
import { useForm } from '~/utils/forms';
import { store } from '~/utils/store';

export function WorkflowTaskDialog({
  taskId,
  workflowId,
  open,
}: {
  taskId: number;
  workflowId: number;
  open: boolean;
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const mainForm = useProcessForm();
  const taskQuery = ProcessService.endpoints.getWorkflowTask.useQuery(
    {
      subtask_id: taskId,
    },
    { skip: !taskId },
  );

  const {
    data: commentList = [],
    // error,
    size,
    setSize,
    isLoading,
    // isValidating,
    mutate,
  } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData?.next) return null; // reached the end
      return { page: pageIndex + 1, id: `tasks-comments-${taskId}` }; // SWR key
    },
    async (args) => {
      const query = store.dispatch(
        ProcessService.endpoints.listAllComments.initiate(
          {
            params: { subtask_id: taskId, page: args.page },
          },
          {
            forceRefetch: true,
            subscribe: false,
          },
        ),
      );
      const comments = await query;
      query.unsubscribe();
      const is404 =
        isFetchBaseQueryError(comments.error) && comments.error.status === 404;
      if (is404) {
        return { results: [], next: false };
      }

      if (comments.isError || !comments.data) {
        throw new Error('Failed to fetch comments');
      }

      return { results: comments.data.results, next: comments.data.next };
    },
    {
      revalidateOnMount: true,
      revalidateFirstPage: true,
      persistSize: true,
    },
  );

  const isLoadingMore =
    isLoading ||
    (size > 0 && commentList && typeof commentList[size - 1] === 'undefined');
  const isEmpty = commentList?.[0]?.results.length === 0;
  const latestPage = Array.isArray(commentList[commentList.length - 1]?.results)
    ? commentList[commentList.length - 1]
    : { results: [], next: false };
  const isEndOfList = isEmpty || !latestPage.next;

  const ListOfComments = commentList.reduce(
    (all, current) => [...all, ...current.results],
    [] as TaskComment[],
  );

  const closeModal = () => {
    mainForm.setValue('selectedTaskId', null);
  };

  //DELETE TASK
  const deleteTask = useWrappedMutation(
    ProcessService.endpoints.deleteWorkflowTask.useMutation(),
  );
  const deleteTaskHandler = async () => {
    await deleteTask.mutateAsync(
      {
        subtask_id: taskId,
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Task Removed' }),
            'success',
          );
          closeModal();
        },
        onError: (err) => {
          createToastNotification(getErrorMessage(err), 'error');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={closeModal} modal={true}>
      <DialogContent size='7xl' className='!bg-gray-100 flex flex-col gap-4'>
        <div className='flex flex-row justify-between items-center '>
          <QueryResolver query={taskQuery} errorElement={<></>}>
            {(data) => (
              <DialogTitle>
                <Breadcrumbs className='border-2 !rounded-md bg-white !p-1'>
                  {data.parent && <ParentButton taskId={data.parent} />}
                  <Button variant='plain' color='neutral' size='sm'>
                    {data.control_id}
                  </Button>
                </Breadcrumbs>
              </DialogTitle>
            )}
          </QueryResolver>
          <div className='flex justify-center gap-4'>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant='soft'
                  color='neutral'
                  startDecorator={<Trash />}
                >
                  <FormattedMessage defaultMessage='Delete Task' />
                </Button>
              </DialogTrigger>
              <DialogContent className='flex flex-col gap-2'>
                <DialogTitle>
                  <FormattedMessage defaultMessage='Are you sure to remove this comment?' />
                </DialogTitle>
                <div className='w-full flex gap-2'>
                  <DialogClose asChild>
                    <Button variant='outlined' color='neutral' fullWidth>
                      <FormattedMessage defaultMessage='Cancel' />
                    </Button>
                  </DialogClose>
                  <Button
                    variant='solid'
                    color='secondary'
                    onClick={deleteTaskHandler}
                    fullWidth
                    loading={deleteTask.state.isLoading}
                  >
                    <FormattedMessage defaultMessage='Confirm' />
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <DialogClose asChild>
              <IconButton size='sm'>
                <X />
              </IconButton>
            </DialogClose>
          </div>
        </div>
        <div className='flex gap-4 items-start'>
          <Card className='!bg-white flex-1 overflow-y-auto max-h-[75vh]'>
            <div className='flex flex-col gap-8'>
              <QueryResolver
                query={taskQuery}
                strategy='stale-while-revalidate'
              >
                {(data) => (
                  <div className='flex flex-col gap-8'>
                    <TaskDetailsForm
                      defaultValues={{
                        id: data.id,
                        name: data.name,
                        description: data.description,
                      }}
                    />

                    {/* SHOW SUBTASK LIST ONLY WHEN TASK IS NOT A SUBTASK */}
                    {!data.parent && (
                      <div className='flex flex-col gap-2'>
                        <div className='flex flex-row justify-between items-center gap-2'>
                          <Typography level='title-lg'>
                            <FormattedMessage defaultMessage='Subtasks' />
                          </Typography>
                        </div>
                        <div>
                          {Array.isArray(data.children) &&
                            [...data.children]
                              ?.sort((a, b) =>
                                a.control_id
                                  .toLowerCase()
                                  .localeCompare(b.control_id.toLowerCase()),
                              )
                              .map((item) => (
                                <div
                                  key={`${item.id}-${item.name}`}
                                  className='flex justify-between cursor-pointer hover:bg-gray-100 p-2 border-y-2 '
                                >
                                  <div
                                    className='flex basis-1/2'
                                    onClick={() =>
                                      mainForm.setValue(
                                        'selectedTaskId',
                                        item.id,
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        mainForm.setValue(
                                          'selectedTaskId',
                                          item.id,
                                        );
                                      }
                                    }}
                                    role='button'
                                    tabIndex={0}
                                    aria-label='Go to Subtask'
                                  >
                                    <div className='basis-1/3'>
                                      <Typography color='neutral'>
                                        {item.control_id}
                                      </Typography>
                                    </div>
                                    <Typography level='title-md'>
                                      {item.name}
                                    </Typography>
                                  </div>
                                  <div className='flex justify-end items-end justify-self-end '>
                                    <div>
                                      <SelectAssignee
                                        assignee={item.assignee_details?.id}
                                        taskId={item.id}
                                        mountOptionsToRoot={true}
                                      />
                                    </div>
                                    <div>
                                      <SelectPriority
                                        priority={item.priority}
                                        taskId={item.id}
                                        mountOptionsToRoot={true}
                                      />
                                    </div>
                                    <div className='basis-40'>
                                      <SelectStatus
                                        status={item.status}
                                        taskId={item.id}
                                        mountOptionsToRoot={true}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                          <AddTaskCard
                            defaultValues={{
                              parent: taskId,
                              template: workflowId,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {/* COMMENTS  */}
                  </div>
                )}
              </QueryResolver>
              <div className='flex flex-col gap-2'>
                <div className='flex flex-row justify-between items-center gap-2'>
                  <Typography level='title-lg'>
                    <FormattedMessage defaultMessage='Comments' />
                  </Typography>
                </div>
                <div className=''>
                  {/*  */}
                  {ListOfComments.map((item) => (
                    <CommentItem item={item} key={item.id} mutate={mutate} />
                  ))}
                  {/*  */}
                  {!isLoading && !isEndOfList && (
                    <Divider>
                      <Button
                        variant='plain'
                        loading={isLoadingMore}
                        onClick={() => setSize(size + 1)}
                      >
                        {isLoadingMore
                          ? intl.formatMessage({
                              defaultMessage: 'Loading More',
                            })
                          : intl.formatMessage({ defaultMessage: 'Load More' })}
                      </Button>
                    </Divider>
                  )}
                  {/*  */}
                  {isEndOfList && (
                    <Divider>
                      <Chip variant='soft' color='neutral' size='sm'>
                        {intl.formatMessage({
                          defaultMessage: 'Reached end of list',
                        })}
                      </Chip>
                    </Divider>
                  )}
                </div>
                <AddUpdateCommentCard
                  defaultValues={{
                    task_id: taskId,
                    show_field: false,
                    content: '',
                    files: [],
                  }}
                  onClose={() => {
                    mutate();
                  }}
                />
              </div>
            </div>
          </Card>
          <div className='basis-1/3 flex flex-col gap-4'>
            <QueryResolver query={taskQuery} strategy='stale-while-revalidate'>
              {(data) => (
                <>
                  <Card className='!bg-white'>
                    <div className='flex flex-col gap-4'>
                      <Typography level='title-lg'>
                        <FormattedMessage defaultMessage='Details' />
                      </Typography>
                      <div>
                        <Label>
                          <FormattedMessage defaultMessage='Task Status' />
                        </Label>
                        <SelectStatus
                          status={data.status}
                          taskId={data.id}
                          mountOptionsToRoot={true}
                        />
                      </div>
                      <div>
                        <Label>
                          <FormattedMessage defaultMessage='Priority' />
                        </Label>
                        <SelectPriority
                          priority={data.priority}
                          taskId={data.id}
                          mountOptionsToRoot={true}
                        />
                      </div>
                      <div>
                        <Label>
                          <FormattedMessage defaultMessage='Assignee' />
                        </Label>
                        <SelectAssignee
                          assignee={data.assignee_details?.id}
                          taskId={data.id}
                          mountOptionsToRoot={true}
                        />
                      </div>
                      <div>
                        <Label>
                          <FormattedMessage defaultMessage='Due Date' />
                        </Label>
                        <SelectDueDate
                          dueDate={data.due_date}
                          taskId={data.id}
                        />
                      </div>
                    </div>
                  </Card>
                  <div>
                    <Typography level='body-sm'>
                      <FormattedMessage defaultMessage='Created at' />{' '}
                      {formatDateString(data.created_at.toString())}
                    </Typography>
                    <Typography level='body-sm'>
                      <FormattedMessage defaultMessage='Updated at' />{' '}
                      {formatDateString(data.updated_at.toString())}
                    </Typography>
                  </div>
                </>
              )}
            </QueryResolver>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// USED IN BREADCRUMBS TO SHOW PARENT CONTROL ID BUTTON WHEN TASK IS A SUBTASKS
const ParentButton = ({ taskId }: { taskId: number }) => {
  const mainForm = useProcessForm();
  const taskQuery = ProcessService.endpoints.getWorkflowTask.useQuery(
    {
      subtask_id: taskId,
    },
    { skip: !taskId },
  );
  return (
    <QueryResolver
      query={taskQuery}
      errorElement={<></>}
      loadingElement={<></>}
    >
      {(data) => (
        <Button
          variant='plain'
          color='neutral'
          size='sm'
          onClick={() => mainForm.setValue('selectedTaskId', data.id)}
        >
          {data.control_id}
        </Button>
      )}
    </QueryResolver>
  );
};

const taskDetailsSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  isUpdatingDescription: z.boolean(),
});

type TaskDetails = z.infer<typeof taskDetailsSchema>;

// CONTAINS DESCRIPTION AND TITLE ONLY
const TaskDetailsForm = ({
  defaultValues,
}: {
  defaultValues: Partial<TaskDetails>;
}) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const updateTask = useWrappedMutation(
    ProcessService.endpoints.updateWorkflowTask.useMutation(),
  );

  const form = useForm({
    schema: taskDetailsSchema,
    defaultValues: { ...defaultValues, isUpdatingDescription: false },
  });

  const isUpdatingDescription = form.watch('isUpdatingDescription');
  const setIsUpdatingDescription = () =>
    form.setValue('isUpdatingDescription', !isUpdatingDescription);

  const updateNameHandler = form.handleSubmit(async (data) => {
    await updateTask.mutateAsync(
      {
        subtask_id: data.id,
        payload: {
          name: data.name,
        },
      },
      {
        onSuccess: () =>
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Task updated successfully.',
            }),
            'success',
          ),
        onError: (err) =>
          createToastNotification(getErrorMessage(err), 'error'),
      },
    );
  });

  const updateDescriptionHandler = form.handleSubmit(async (data) => {
    await updateTask.mutateAsync(
      {
        subtask_id: data.id,
        payload: {
          description: data.description,
        },
      },
      {
        onSuccess: () =>
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Task updated successfully.',
            }),
            'success',
          ),
        onError: (err) =>
          createToastNotification(getErrorMessage(err), 'error'),
      },
    );
  });

  return (
    <form noValidate>
      <FieldGroup>
        <LoadingWrapper loading={updateTask.state.isLoading}>
          <InputField
            control={form.control}
            field='name'
            size='lg'
            variant='plain'
            className='!text-2xl !font-semibold !-ml-4'
            onBlur={updateNameHandler}
          />
        </LoadingWrapper>
        {!isUpdatingDescription ? (
          <div>
            <div
              className='prose prose-base max-w-none hover:bg-gray-100'
              dangerouslySetInnerHTML={{ __html: form.watch('description') }}
              onClick={setIsUpdatingDescription}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsUpdatingDescription();
                }
              }}
              role='button'
              tabIndex={0}
              aria-label='Edit Description'
            />
          </div>
        ) : (
          <Field size='lg' control={form.control} field='description'>
            <div className={clsx(inputFieldClasses)}>
              <div
                data-slot='label'
                className='flex items-center justify-between'
              >
                <Label required={true}>
                  <FormattedMessage defaultMessage='Description' />
                </Label>
              </div>
              <RichTextEditorField
                otherActions={
                  <div className='flex gap-2'>
                    <Button
                      variant='plain'
                      color='neutral'
                      onClick={setIsUpdatingDescription}
                    >
                      <FormattedMessage defaultMessage='Cancel' />
                    </Button>
                    <Button
                      variant='outlined'
                      color='gradient-primary'
                      onClick={updateDescriptionHandler}
                      loading={updateTask.state.isLoading}
                    >
                      <FormattedMessage defaultMessage='Save' />
                    </Button>
                  </div>
                }
              />
            </div>
          </Field>
        )}
      </FieldGroup>
    </form>
  );
};

// COMPONENT TO VIEW SINGLE COMMENT

const CommentItem = ({
  item,
  mutate,
}: {
  item: TaskComment;
  mutate: () => void;
}) => {
  const intl = useIntl();

  const [showOptions, setShowOptions] = useState(false);
  const [updateComment, setUpdateComment] = useState(false);
  const updateCommentToggle = () => setUpdateComment((prev) => !prev);
  const showOptionToggle = () => setShowOptions((prev) => !prev);

  const { createToastNotification } = useToastNotificationContext();
  const deleteComment = useWrappedMutation(
    ProcessService.endpoints.deleteTaskComment.useMutation(),
  );

  const deleteCommentHandler = () => {
    deleteComment.mutateAsync(
      {
        comment_id: item.id,
        params: {
          subtask_id: item.subtask,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Comment deleted.' }),
            'success',
          );
        },
        onError: (err) => {
          createToastNotification(getErrorMessage(err), 'error');
        },
      },
    );
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row justify-between items-center'>
        <div className='flex flex-row justify-center items-center w-fit'>
          <Typography color='neutral' level='title-lg'>
            {item.author_details.full_name}
          </Typography>
          <Dot size={32} />
          <Typography level='body-sm'>
            {new Date(item.created_at).toLocaleString()}
          </Typography>
        </div>
        <Popover
          modal={true}
          open={showOptions}
          onOpenChange={showOptionToggle}
        >
          <PopoverTrigger asChild>
            <IconButton>
              <DotsThree />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent className='!z-50'>
            <div className='flex flex-col'>
              <Button
                variant='plain'
                color='neutral'
                className='flex !justify-start'
                startDecorator={<PencilSimpleLine />}
                onClick={() => {
                  updateCommentToggle();
                  showOptionToggle();
                }}
              >
                <FormattedMessage defaultMessage='Edit' />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant='plain'
                    color='neutral'
                    className='flex !justify-start'
                    startDecorator={<Trash />}
                  >
                    <FormattedMessage defaultMessage='Delete Comment' />
                  </Button>
                </DialogTrigger>
                <DialogContent className='flex flex-col gap-2'>
                  <DialogTitle>
                    <FormattedMessage defaultMessage='Are you sure to remove this comment?' />
                  </DialogTitle>
                  <div className='w-full flex gap-2'>
                    <DialogClose asChild>
                      <Button variant='outlined' color='neutral' fullWidth>
                        <FormattedMessage defaultMessage='Cancel' />
                      </Button>
                    </DialogClose>
                    <Button
                      variant='solid'
                      color='secondary'
                      onClick={deleteCommentHandler}
                      fullWidth
                      loading={deleteComment.state.isLoading}
                    >
                      <FormattedMessage defaultMessage='Confirm' />
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {updateComment ? (
        <AddUpdateCommentCard
          defaultValues={{
            task_id: item.subtask,
            comment_id: item.id,
            content: item.content,
            show_field: true,
            files: [],
          }}
          onClose={() => {
            mutate();
            updateCommentToggle();
          }}
        />
      ) : (
        <div className='flex flex-col gap-4'>
          <div
            className='prose prose-base max-w-none hover:bg-gray-100'
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
          <div className='flex flex-row gap-2 pl-4'>
            {item.report_files_data.map((file) => (
              <div key={`comment-attachment-${file.id}`}>
                <ReportAttachmentItem {...file} />
              </div>
            ))}
            {item.files.map((file) => (
              <div key={`uploaded-comment-attachment-${file.id}`}>
                <ReportAttachmentItem {...file} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const addTaskSchema = z.object({
  template: z.number(),
  parent: z.number(),
  name: z.string().min(1, 'this is required'),
  description: z.string().min(1, 'this is required'),
});

// COMPONENT ON ADDING COMMENTS

type AddTask = z.infer<typeof addTaskSchema>;

const AddTaskCard = ({
  defaultValues,
}: {
  defaultValues: Partial<AddTask>;
}) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [showAddTask, setShowAddTask] = useState(false);

  const createTask = useWrappedMutation(
    ProcessService.endpoints.addWorkflowTask.useMutation(),
  );

  const form = useForm({
    schema: addTaskSchema,
    defaultValues: {
      ...defaultValues,
      name: '',
      description: '',
    },
  });

  const addTaskHandler = form.handleSubmit(async (data) => {
    await createTask.mutateAsync(
      {
        payload: {
          parent: data.parent,
          template: data.template,
          name: data.name,
          description: data.description,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Subtask Created Successfully',
            }),
            'success',
          );
          setShowAddTask((prev) => !prev);
        },
        onError: (err) => {
          createToastNotification(getErrorMessage(err), 'error');
        },
      },
    );
  });

  return (
    <>
      {showAddTask ? (
        <Card>
          <form onSubmit={addTaskHandler}>
            <FieldGroup>
              <InputField
                control={form.control}
                field='name'
                placeholder={intl.formatMessage({
                  defaultMessage: 'Subtask Name',
                })}
              />
              <Field size='lg' control={form.control} field='description'>
                <div className={clsx(inputFieldClasses)}>
                  <RichTextEditorField
                    otherActions={
                      <div className='flex gap-2'>
                        <Button
                          variant='plain'
                          color='neutral'
                          onClick={() => setShowAddTask((prev) => !prev)}
                        >
                          <FormattedMessage defaultMessage='Cancel' />
                        </Button>
                        <Button
                          variant='outlined'
                          color='gradient-primary'
                          type='submit'
                          loading={createTask.state.isLoading}
                        >
                          <FormattedMessage defaultMessage='Create' />
                        </Button>
                      </div>
                    }
                  />
                </div>
              </Field>
            </FieldGroup>
          </form>
        </Card>
      ) : (
        <Button
          variant='plain'
          color='neutral'
          size='sm'
          startDecorator={<Plus />}
          onClick={() => {
            setShowAddTask((prev) => !prev);
          }}
        >
          <FormattedMessage defaultMessage='Add Subtask' />
        </Button>
      )}
    </>
  );
};

const commentSchema = z.object({
  task_id: z.number(),
  comment_id: z.number().optional(),
  content: z.string().min(1, 'this is required'),
  files: z.array(
    z.object({
      file_id: z.number(),
      file_name: z.string(),
      is_uploaded: z.boolean(),
    }),
  ),
  show_field: z.boolean(),
});

type Comment = z.infer<typeof commentSchema>;

const AddUpdateCommentCard = ({
  defaultValues,
  onClose,
}: {
  defaultValues: Partial<Comment>;
  onClose?: () => void;
}) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();

  const form = useForm({
    schema: commentSchema,
    defaultValues,
  });
  const showField = form.watch('show_field');
  const showAddToggle = () => form.setValue('show_field', !showField);

  const addComment = useWrappedMutation(
    ProcessService.endpoints.addTaskComment.useMutation(),
  );
  const updateComment = useWrappedMutation(
    ProcessService.endpoints.updateTaskComment.useMutation(),
  );

  const addCommentHandler = form.handleSubmit(async (data) => {
    // WHEN NO comment_id IN FORM CALL POST QUERY AND UPDATE QUERY OTHERWISE
    if (data.comment_id) {
      await updateComment.mutateAsync(
        {
          comment_id: data.comment_id,
          params: {
            subtask_id: data.task_id,
          },
          payload: {
            content: data.content,
          },
        },
        {
          onSuccess: () => {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Comment Updated Successfully',
              }),
              'success',
            );
            form.reset();
            showAddToggle();
            if (onClose) onClose();
          },
          onError: (err) => {
            createToastNotification(getErrorMessage(err), 'error');
          },
        },
      );
    } else {
      await addComment.mutateAsync(
        {
          payload: {
            content: data.content,
            subtask: data.task_id,
            file_ids: data.files
              .filter((f) => f.is_uploaded)
              .map((file) => file.file_id),
            report_files: data.files
              .filter((f) => !f.is_uploaded)
              .map((file) => file.file_id),
          },
        },
        {
          onSuccess: () => {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Comment Added Successfully',
              }),
              'success',
            );
            form.reset();
            showAddToggle();
            if (onClose) onClose();
          },
          onError: (err) => {
            createToastNotification(getErrorMessage(err), 'error');
          },
        },
      );
    }
  });

  return (
    <div>
      {showField ? (
        <form noValidate onSubmit={addCommentHandler}>
          <Field size='lg' control={form.control} field='content'>
            <div className={clsx(inputFieldClasses)}>
              <RichTextEditorField
                otherActions={
                  <div className='flex gap-2'>
                    {!defaultValues?.comment_id && (
                      <Field control={form.control} field='files'>
                        <AttachFilebutton />
                      </Field>
                    )}
                    <Button
                      variant='plain'
                      color='neutral'
                      onClick={() => {
                        showAddToggle();
                        if (onClose) onClose();
                      }}
                      disabled={addComment.state.isLoading}
                    >
                      <FormattedMessage defaultMessage='Cancel' />
                    </Button>
                    <Button
                      variant='solid'
                      color='secondary'
                      type='submit'
                      loading={addComment.state.isLoading}
                    >
                      {form.watch('comment_id') ? (
                        <FormattedMessage defaultMessage='Update' />
                      ) : (
                        <FormattedMessage defaultMessage='Comment' />
                      )}
                    </Button>
                  </div>
                }
              />
            </div>
          </Field>
        </form>
      ) : (
        <div
          className='flex p-4 border-2 rounded-lg cursor-pointer'
          onClick={showAddToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              showAddToggle();
            }
          }}
          role='button'
          tabIndex={0}
          aria-label='Add Comment'
        >
          <Typography color='neutral'>
            <FormattedMessage defaultMessage='Leave a comment...' />
          </Typography>
        </div>
      )}
    </div>
  );
};

export const RichTextEditorField = ({
  variant = 'outlined',
  otherActions,
}: Pick<CardProps, 'variant'> & { otherActions?: ReactNode }) => {
  const controller = useFormController();
  const isInvalid = !!controller?.fieldState.invalid;

  return (
    <RichTextEditor>
      <Card
        size='sm'
        color={isInvalid ? 'danger' : 'neutral'}
        variant={variant}
      >
        <RichTextEditorContent />
        <Divider />
        <div className='flex items-center justify-between w-full'>
          <div className='flex items-center gap-2'>
            <EditorControls.Formatting />
            <Divider orientation='vertical' />
            <HeadingMenu />
            <FontSizeMenu />
            <Divider orientation='vertical' />
            <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
              <EditorControls.Bold />
              <EditorControls.Underline />
              <EditorControls.Italic />
            </ToggleButtonGroup>
            <Divider orientation='vertical' />
            <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
              <EditorControls.OrderedList />
              <EditorControls.UnorderedList />
            </ToggleButtonGroup>
          </div>
          {otherActions}
        </div>
      </Card>
    </RichTextEditor>
  );
};
